import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/hooks/use-auth";
import { useDebounce } from "@/hooks/use-debounce";
import { format } from "date-fns";
import {
  Search,
  Eye,
  MoreHorizontal,
  Gift,
  Package,
  Plane,
  Hotel,
  Plus,
} from "lucide-react";
import { toast } from "sonner";
import { PaginationControls } from "@/components/common/PaginationControl";
import { Link } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { ProductApi } from "@/api/product.api";
import { BrandApi } from "@/api/brand.api";
import { RedemptionMerchantApi } from "@/api/redemptionMerchant.api";
import { Badge } from "@/components/ui/badge";
import { ProductTableSkeleton } from "@/components/transactions/ProductTableSkeleton";
import { usePlatformCategories } from "@/contexts/PlatformCategoryContext";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { GiftCardTableSkeleton } from "@/components/transactions/GiftCardTableSkeleton";
import { Dialog, DialogTrigger, DialogContent } from "@/components/ui/dialog";
import GiftCardProductCreatePage from "./gift-card-create";
import ProductTabsNav from "./ProductTabsNav";
import GiftCardProductGridSkeleton from "./GiftCardProductGridSkeleton";
import { ProductFilters } from "@/components/common/ProductFilters";

interface GiftCardProduct {
  id: number;
  createdDate: string;
  brandVO?: {
    brandName?: string;
    brandImageUri?: string;
  };
  redemptionMerchantVO?: {
    merchantName?: string;
    countryVO?: {
      name?: string;
    };
  };
  productCode: string;
  productName: string;
  productImageUri?: string;
  platformCategoryIds?: number[];
  minValue?: number;
  maxValue?: number;
  denominationIncrement?: number;
  currencyVO?: {
    iso3CurrencyCode?: string;
    currencySymbol?: string;
  };
}

interface GiftCardResponse {
  giftCardProductVOs: GiftCardProduct[];
  totalRecords: number;
}

interface Column {
  header: string;
  accessorKey?: keyof GiftCardProduct;
  id?: string;
  cell?: (row: GiftCardProduct) => React.ReactNode;
}

const ProductsPage = () => {
  const { refreshToken } = useAuth();
  const [products, setProducts] = useState<GiftCardProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalProducts, setTotalProducts] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("createdDate");
  const [sortOrder, setSortOrder] = useState("desc");
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const { getCategoryNames } = usePlatformCategories();
  const [filters, setFilters] = useState<any>({});
  const [searchType, setSearchType] = useState("productCode");
  const [searchTerm, setSearchTerm] = useState("");
  const [brands, setBrands] = useState([]);
  const [merchants, setMerchants] = useState([]);

  const getFileUrl = (uri) => {
    if (!uri) return "/placeholder.svg";
    if (uri.startsWith("http")) return uri;
    if (uri.startsWith("/")) {
      return `${import.meta.env.VITE_REACT_APP_CORE_ENGINE_URL}${uri}`;
    }
    return `${
      import.meta.env.VITE_REACT_APP_CORE_ENGINE_URL
    }/file/download?fileUri=${encodeURIComponent(uri)}`;
  };

  const handleApiError = async (response: Response) => {
    if (response.status === 401 || response.status === 403) {
      const refreshSuccess = await refreshToken();
      if (refreshSuccess) {
        return true;
      }
      throw new Error("Authentication failed");
    }
    return false;
  };

  const fetchGiftCards = async (
    overrideFilters = filters,
    overrideSearchType = searchType,
    overrideSearchTerm = searchTerm
  ) => {
    try {
      setLoading(true);
      setError(null);

      // Determine search field based on search type
      const searchField =
        overrideSearchType === "productCode" ? "productCode" : "productName";

      const payload = {
        [searchField]: overrideSearchTerm || null,
        productName:
          overrideSearchType === "productName" ? overrideSearchTerm : null,
        productCode:
          overrideSearchType === "productCode" ? overrideSearchTerm : null,
        redemptionMerchantId: overrideFilters.redemptionMerchantId || null,
        brandId: overrideFilters.brandId || null,
        countryId: null,
        platformCategoryId: overrideFilters.platformCategoryId || null,
        productType: "GIFT_CARD",
        pageNumber: currentPage,
        pageSize,
        sortOrder,
        sortBy,
      };
      const { data } = await ProductApi.fetchGiftCardProducts(payload);
      setProducts(data.giftCardProductVOs || []);
      setTotalProducts(data.totalRecords || 0);
    } catch (error) {
      setError(error?.message || "Failed to fetch gift cards");
      toast.error(error?.message || "Failed to fetch gift cards");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGiftCards();
  }, [currentPage, pageSize, debouncedSearchQuery, sortBy, sortOrder]);

  useEffect(() => {
    fetchGiftCards(filters, searchType, searchTerm);
  }, [searchTerm, searchType]);

  useEffect(() => {
    fetchBrands();
    fetchMerchants();
  }, []);

  const fetchBrands = async () => {
    try {
      const { data } = await BrandApi.fetchAllBrands();
      setBrands(data || []);
    } catch (error) {
      console.error("Error fetching brands:", error);
    }
  };

  const fetchMerchants = async () => {
    try {
      const res = await fetch(
        `${
          import.meta.env.VITE_REACT_APP_CORE_ENGINE_URL
        }/redemptionMerchant/getRedemptionMerchants`,
        {
          method: "POST",
          headers: {
            accept: "application/json, text/plain, */*",
            "content-type": "application/json",
          },
          body: JSON.stringify({
            merchantName: null,
            merchantCode: null,
            warehouseCountryId: null,
            pageNumber: 0,
            pageSize: 100,
            sortOrder: "desc",
            sortBy: "createdDate",
          }),
          credentials: "include",
        }
      );
      const data = await res.json();
      console.log("Raw merchants API response:", data);
      const merchantsArray = data.redemptionMerchantVOS || [];
      console.log("Processed merchants array:", merchantsArray);
      console.log("Merchants array length:", merchantsArray.length);
      setMerchants(merchantsArray);
    } catch (error) {
      console.error("Error fetching merchants:", error);
      setMerchants([]);
    }
  };

  const handleFilterChange = (newFilters: any) => {
    setFilters(newFilters);
    setCurrentPage(0);
    fetchGiftCards(newFilters, searchType, searchTerm);
  };

  const handleReset = () => {
    setSearchTerm("");
    setSearchType("productCode");
    setFilters({});
    setCurrentPage(0);
    fetchGiftCards({}, "productCode", "");
  };

  const formatDenomination = (product: GiftCardProduct) => {
    const { minValue, maxValue, denominationIncrement, currencyVO } = product;
    if (!minValue || !maxValue) return "-";

    const currency =
      currencyVO?.currencySymbol || currencyVO?.iso3CurrencyCode || "";
    const increment = denominationIncrement || 1;

    if (minValue === maxValue) {
      return `${currency}${minValue}`;
    }

    return `${currency}${minValue} - ${currency}${maxValue} (${currency}${increment} increments)`;
  };

  const columns: Column[] = [
    {
      header: "Create Date",
      accessorKey: "createdDate",
      cell: (row) => (
        <div>
          {row.createdDate
            ? format(new Date(row.createdDate), "MMM dd, yyyy")
            : "-"}
        </div>
      ),
    },
    {
      header: "Logo",
      cell: (row) => (
        <div className="w-10 h-10 rounded-md bg-gray-100 flex items-center justify-center">
          <img
            src={getFileUrl(row.productImageUri || row.brandVO?.brandImageUri)}
            alt={row.productName}
            className="max-h-8 max-w-8 object-contain"
            onError={(e) => {
              e.currentTarget.src = "/placeholder.svg";
            }}
          />
        </div>
      ),
    },
    {
      header: "Brand",
      cell: (row) => <div>{row.brandVO?.brandName || "-"}</div>,
    },
    {
      header: "Merchant",
      cell: (row) => <div>{row.redemptionMerchantVO?.merchantName || "-"}</div>,
    },
    {
      header: "Product Code",
      accessorKey: "productCode",
      cell: (row) => (
        <div className="font-mono text-sm">{row.productCode || "-"}</div>
      ),
    },
    {
      header: "Product Name",
      accessorKey: "productName",
      cell: (row) => (
        <div className="font-medium">{row.productName || "-"}</div>
      ),
    },
    {
      header: "Country",
      cell: (row) => (
        <div>{row.redemptionMerchantVO?.countryVO?.name || "-"}</div>
      ),
    },
    {
      header: "Platform Category",
      cell: (row) => (
        <div>
          {row.platformCategoryIds && row.platformCategoryIds.length > 0 ? (
            <Badge variant="outline" className="bg-blue-50 text-blue-700">
              {getCategoryNames(row.platformCategoryIds).join(", ") ||
                `Category ${row.platformCategoryIds[0]}`}
            </Badge>
          ) : (
            "-"
          )}
        </div>
      ),
    },
    {
      header: "Denomination",
      cell: (row) => <div className="text-sm">{formatDenomination(row)}</div>,
    },
    {
      header: "Actions",
      cell: (row) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8 p-0">
              <span className="sr-only">Open menu</span>
              <MoreHorizontal className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem asChild>
              <Link to={`/rewardpoint/products/gift-card-view/${row.id}`}>
                View Details
              </Link>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Products</h1>
          <p className="text-gray-500">Manage different types of products</p>
        </div>
        <Link to="/rewardpoint/products/gift-cards/create">
          <Button>
            <Plus className="mr-2 h-4 w-4" /> Add Product
          </Button>
        </Link>
      </div>

      {/* Product Type Navigation */}
      <div className="flex justify-start">
        <Tabs defaultValue="gift-cards">
          <TabsList className="flex gap-2">
            <TabsTrigger value="gift-cards" className="flex items-center gap-2">
              <Gift className="h-4 w-4" />
              Gift Cards
            </TabsTrigger>
            <TabsTrigger
              value="merchandise"
              asChild
              className="flex items-center gap-2"
            >
              <Link
                to="/rewardpoint/products/merchandise"
                className="flex items-center gap-2"
              >
                <Package className="h-4 w-4" />
                Merchandise
              </Link>
            </TabsTrigger>
            <TabsTrigger
              value="flight-booking"
              asChild
              className="flex items-center gap-2"
            >
              <Link
                to="/rewardpoint/products/flight-booking"
                className="flex items-center gap-2"
              >
                <Plane className="h-4 w-4" />
                Flight Booking
              </Link>
            </TabsTrigger>
            <TabsTrigger
              value="hotel-booking"
              asChild
              className="flex items-center gap-2"
            >
              <Link
                to="/rewardpoint/products/hotel-booking"
                className="flex items-center gap-2"
              >
                <Hotel className="h-4 w-4" />
                Hotel Booking
              </Link>
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Gift Cards Content */}
      <div className="mt-6">
        {/* Filters */}
        <ProductFilters
          onFilterChange={handleFilterChange}
          onReset={handleReset}
          brands={brands}
          merchants={merchants}
          searchType={searchType}
          setSearchType={setSearchType}
          searchTerm={searchTerm}
          onSearch={setSearchTerm}
        />

        {/* Table */}
        {loading ? (
          <GiftCardProductGridSkeleton />
        ) : (
          <DataTable
            title="All Gift Cards"
            data={products}
            columns={columns}
            customTableBody={undefined}
          />
        )}

        {/* Pagination */}
        <div className="flex items-center justify-between mt-4">
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-500">Rows per page:</span>
            <Select
              value={pageSize.toString()}
              onValueChange={(value) => {
                setPageSize(Number(value));
                setCurrentPage(0);
              }}
            >
              <SelectTrigger className="w-[80px]">
                <SelectValue placeholder={pageSize.toString()} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="20">20</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <PaginationControls
            totalPages={Math.ceil(totalProducts / pageSize)}
            currentPage={currentPage}
            onNextHandler={() =>
              setCurrentPage((p) =>
                Math.min(Math.ceil(totalProducts / pageSize) - 1, p + 1)
              )
            }
            onPrevHandler={() => setCurrentPage((p) => Math.max(0, p - 1))}
            onPageChange={(page) => setCurrentPage(page)}
          />
        </div>
      </div>
    </div>
  );
};

export default ProductsPage;

export { default as FlightBookingProductCreatePage } from "./flight-booking-create";
